import React, { useState } from "react";
import {
  SafText,
  SafTable,
  SafIcon,
  SafButton,
  SafCheckbox,
  SafOption,
  SafCombobox,
  SafAlert,
  SafAnchor,
  SafPagination,
  SafTooltip,
} from "@saffron/core-components/react";
import { useMemo, useCallback, useEffect } from "react";
import { callMiddleware } from "../../../util/api";
import { useParams } from "react-router-dom";

const TechnologyStackComparison = ({ selectedCloudProvider = "" }) => {
  const [runtimeData, setRuntimeData] = useState([]);
  const [filteredIds, setFilteredIds] = useState([]);
  const { assetId: urlAssetId } = useParams();

  // State for managing selected technologies and their properties
  const [selectedTechnologies, setSelectedTechnologies] = useState({});
  const [stackTypeSelections, setStackTypeSelections] = useState({});
  const [selectedPatterns, setSelectedPatterns] = useState({});
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState(null);
  const [openAIAlertOptions, setOpenAIAlertOptions] = useState(undefined);
  const [aiAlertMessage, setAIAlertMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [techDetailsMap, setTechDetailsMap] = useState({});
  const [lastRefreshed, setLastRefreshed] = useState(null);

  const fetchTechStackDetails = async () => {
    try {
      const dbName = process.env.REACT_APP_DB_SOURCE;
      const containerId = "tech-stack-mapping";
      const query = "SELECT * FROM c";

      const resources = await callMiddleware("get", {
        dbName,
        containerName: containerId,
        query,
      });

      // Create ONE global pattern lookup for all services
      const globalPatternLookup = {};
      const techDetailsMapLocal = {};

      if (Array.isArray(resources)) {
        resources.forEach((item) => {
          if (item.capabilities && Array.isArray(item.capabilities)) {
            item.capabilities.forEach((capability) => {
              if (capability.services && Array.isArray(capability.services)) {
                capability.services.forEach((service) => {
                  // Build the global pattern lookup using pattern_ref_id as key
                  if (
                    service.supportingPattern &&
                    Array.isArray(service.supportingPattern)
                  ) {
                    service.supportingPattern.forEach((pattern) => {
                      globalPatternLookup[pattern.pattern_ref_id] = pattern;
                    });
                  }

                  // Store service details without individual pattern lookup
                  techDetailsMapLocal[service.tech_radar_id] = {
                    categoryName: item.category || "",
                    technologyName: service.technology_name || "",
                    capability: capability.capability || "",
                    recommendation: service.recommendation || "",
                    provider: service.provider || "",
                    description: service.description || "",
                    supportingPattern: service.supportingPattern || [],
                    tech_radar_id: service.tech_radar_id || "",
                  };
                });
              }
            });
          }
        });
      }

      // Store both the tech details and the global pattern lookup
      setTechDetailsMap({
        ...techDetailsMapLocal,
        _globalPatternLookup: globalPatternLookup,
      });
    } catch (error) {
      console.error("Error fetching tech stack details from middleware", error);
    }
  };

  const stackTypeOptions = useMemo(
    () => [
      { label: "Front-end", id: 1 },
      { label: "Back-end", id: 2 },
      { label: "Devops", id: 3 },
    ],
    []
  );

  // Handle technology selection checkbox
  const handleCheckboxChange = (techId) => {
    setSelectedTechnologies((prev) => {
      const newSelected = { ...prev };
      if (newSelected[techId]) {
        delete newSelected[techId];
        // Also clean up related state
        setStackTypeSelections((prevStack) => {
          const newStack = { ...prevStack };
          delete newStack[techId];
          return newStack;
        });
        setSelectedPatterns((prevPatterns) => {
          const newPatterns = { ...prevPatterns };
          delete newPatterns[techId];
          return newPatterns;
        });
      } else {
        newSelected[techId] = true;
      }
      return newSelected;
    });
  };

  // Handle pattern checkbox toggle for a specific technology
  const handlePatternCheckboxChange = (techId, patternIndex, isChecked) => {
    setSelectedPatterns((prev) => {
      const updated = { ...prev };
      if (!updated[techId]) {
        updated[techId] = [];
      }

      if (isChecked) {
        if (!updated[techId].includes(patternIndex)) {
          updated[techId] = [...updated[techId], patternIndex];
        }
      } else {
        updated[techId] = updated[techId].filter(
          (index) => index !== patternIndex
        );
      }

      if (updated[techId].length === 0) {
        delete updated[techId];
      }

      return updated;
    });
  };

  // Handle stack type change for a specific technology (supports multiple selections)
  const handleStackTypeChange = (techId, value) => {
    setStackTypeSelections((prev) => {
      const currentSelections = prev[techId] || [];
      let updatedSelections;

      if (currentSelections.includes(value)) {
        // Remove if already selected
        updatedSelections = currentSelections.filter((item) => item !== value);
      } else {
        // Add to selections
        updatedSelections = [...currentSelections, value];
      }

      return {
        ...prev,
        [techId]: updatedSelections,
      };
    });
  };

  const handleRefresh = async () => {
    try {
      setIsLoading(true);
      await invokeTechComparisonFunction(urlAssetId);
    } catch (err) {
      setAlertMessage(
        "Failed to refresh technology stack data. Please try again later."
      );
      setAlertType("error");
      setTimeout(() => setAlertMessage(""), 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const invokeTechComparisonFunction = async (assetId) => {
    try {
      setOpenAIAlertOptions(["toast", "info"]);
      setAIAlertMessage("Refreshing Mismatched Technologies");
      const controller = new AbortController();
      setTimeout(() => controller.abort(), 15 * 60 * 1000);

      const response = await fetch(
        process.env.REACT_APP_AI_TECHCOMPARISION_ENDPOINT,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            asset_id: assetId,
          }),
          signal: controller.signal,
        }
      );

      // clearTimeout(timeoutId);

      if (!response.ok) {
        const errorJson = await response.json();
        if (errorJson && errorJson.message) {
          setOpenAIAlertOptions(["toast", "info"]);
          setAIAlertMessage(errorJson.message);
          setTimeout(() => setOpenAIAlertOptions(undefined), 3000);
          return;
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      }

      const result = await response.json();

      setTimeout(() => {
        if (result.status === "success") {
          setTimeout(() => {
            fetchTechnologyStacksData();
          }, 2000);
        } else {
          setOpenAIAlertOptions(["toast", "error"]);
          setAIAlertMessage("Technology comparison failed. Please try again.");
        }
        setTimeout(() => setOpenAIAlertOptions(undefined), 5000);
      }, 100);
    } catch (error) {
      console.error("Error invoking tech comparison function:", error);
      setOpenAIAlertOptions(undefined);

      setTimeout(() => {
        // Handle 504 specifically
        if (
          error.message.includes("504") ||
          error.message.includes("gateway")
        ) {
          setOpenAIAlertOptions(["toast", "warning"]);
          setAIAlertMessage(
            "The request timed out due to gateway settings. However, the analysis is still running in the background and should complete in 7-10 minutes. Please refresh the page after a few minutes to see the results."
          );
        } else if (error.name === "AbortError") {
          setOpenAIAlertOptions(["toast", "warning"]);
          setAIAlertMessage(
            "Request exceeded 15 minutes. Please check back later."
          );
        } else {
          setOpenAIAlertOptions(["toast", "error"]);
          setAIAlertMessage(
            "Failed to run technology comparison. Please try again."
          );
        }
        setTimeout(() => setOpenAIAlertOptions(undefined), 10000);
      }, 100);
    }
  };
  // Add handler (no dialog)
  const handleAdd = () => {
    handleConfirmAdd();
  };

  // Called when Add is clicked (no dialog)
  const handleConfirmAdd = async () => {
    // Validate that at least one technology is selected
    const selectedTechIds = Object.keys(selectedTechnologies).filter(
      (techId) => selectedTechnologies[techId]
    );

    if (selectedTechIds.length === 0) {
      setAlertMessage("Please select at least one technology to add.");
      setAlertType("warning");
      setTimeout(() => setAlertMessage(""), 3000);
      return;
    }

    // Validate that all selected technologies have a stack type
    const missingStackType = selectedTechIds.filter(
      (techId) =>
        !stackTypeSelections[techId] || stackTypeSelections[techId].length === 0
    );

    if (missingStackType.length > 0) {
      setAlertMessage(
        missingStackType.length === 1
          ? `Please select a Stack Type for the selected technology.`
          : `Please select a Stack Type for all selected technologies.`
      );
      setAlertType("warning");
      setTimeout(() => setAlertMessage(""), 3000);
      return;
    }

    try {
      setIsLoading(true);

      const dbName = process.env.REACT_APP_DB_TARGET;
      const containerName = process.env.REACT_APP_CONTAINER_ASSET_PARTITION;
      const query = `SELECT * FROM c WHERE c.asset_id = '${urlAssetId}' AND c.id = 'technology-stacks'`;

      const existingDataResult = await callMiddleware("get", {
        dbName,
        containerName,
        query,
        partitionKey: urlAssetId,
      });

      let techStackData;

      if (
        existingDataResult &&
        Array.isArray(existingDataResult) &&
        existingDataResult.length > 0
      ) {
        techStackData = existingDataResult[0];
      } else if (
        existingDataResult &&
        existingDataResult.id === "technology-stacks"
      ) {
        techStackData = existingDataResult;
      } else {
        // Create new document structure
        techStackData = {
          id: "technology-stacks",
          asset_id: urlAssetId,
          technology_stacks: [],
          deviations: [],
          createdAt: new Date().toISOString(),
          last_modified: new Date().toISOString(),
        };
      }

      // Process each selected technology
      selectedTechIds.forEach((techId) => {
        const tech = filteredData.find((t) => {
          // For techDetailsMap entries
          if (t.tech_radar_id && t.tech_radar_id === techId) return true;
          // For runtimeData entries (no tech_radar_id)
          if (!t.tech_radar_id && techId === null && t.technologyName)
            return true;
          return false;
        });
        if (!tech) return;

        const selectedStackTypes = stackTypeSelections[techId] || [];
        const selectedPatternIndices = selectedPatterns[techId] || [];
        const envValue = "Proposed";

        // Process each selected stack type for this technology
        selectedStackTypes.forEach((displayStackType) => {
          let stackType = displayStackType;
          if (stackType === "Front-end") {
            stackType = "frontend";
          } else if (stackType === "Back-end") {
            stackType = "backend";
          } else if (stackType === "Devops") {
            stackType = "devops";
          }

          // If tech_radar_id is present, use old logic
          if (tech.tech_radar_id && !isNaN(tech.tech_radar_id)) {
            // Filter supporting patterns to include only selected ones
            const filteredSupportingPattern = tech.supportingPattern
              ? tech.supportingPattern.filter((_, index) =>
                  selectedPatternIndices.includes(index)
                )
              : [];

            // Create service object with selected patterns
            const service = {
              service_id: tech.tech_radar_id,
              pattern_ref_id:
                filteredSupportingPattern.map(
                  (pattern) => pattern.pattern_ref_id
                ) || [],
            };

            // Find or create the tech stack
            let techStack = techStackData.technology_stacks?.find(
              (stack) => stack.tech_stack === stackType
            );

            if (!techStack) {
              techStack = {
                tech_stack: stackType,
                tech_stack_environment: [],
              };
              techStackData.technology_stacks =
                techStackData.technology_stacks || [];
              techStackData.technology_stacks.push(techStack);
            }

            // Find or create the environment
            let environment = techStack.tech_stack_environment?.find(
              (env) => env.environment === envValue
            );

            if (environment) {
              // Convert old structure to new structure if needed
              if (!environment.services && environment.service_ids) {
                environment.services = environment.service_ids.map((id) => ({
                  service_id: id,
                  pattern_ref_id: [],
                }));
                delete environment.service_ids;
              }

              if (!environment.services) {
                environment.services = [];
              }

              // Add new service, avoiding duplicates
              const existingService = environment.services.find(
                (s) => s.service_id === service.service_id
              );

              if (!existingService) {
                environment.services.push(service);
              } else {
                // Merge pattern_ref_id if service already exists
                const existingPatternRefIds =
                  existingService.pattern_ref_id || [];
                const newPatternRefIds = service.pattern_ref_id || [];

                newPatternRefIds.forEach((patternRefId) => {
                  if (!existingPatternRefIds.includes(patternRefId)) {
                    existingService.pattern_ref_id.push(patternRefId);
                  }
                });
              }
            } else {
              // Create new environment
              techStack.tech_stack_environment =
                techStack.tech_stack_environment || [];
              techStack.tech_stack_environment.push({
                environment: envValue,
                score: "coming soon",
                services: [service],
              });
            }

            // Add to deviations if not Adopt or Mandate
            if (
              envValue === "Proposed" &&
              tech.recommendation !== "Adopt" &&
              tech.recommendation !== "Mandate"
            ) {
              if (!techStackData.deviations) {
                techStackData.deviations = [];
              }

              if (
                !techStackData.deviations.some(
                  (d) => d.service_id === tech.tech_radar_id
                )
              ) {
                techStackData.deviations.push({
                  service_id: tech.tech_radar_id,
                  justification: "",
                });
              }
            }
          } else {
            // For entries with no tech_radar_id (from runtimeData)
            // Use service_name instead of service_id
            const serviceName = tech.technologyName;
            if (!serviceName) return;
            const service = {
              service_name: serviceName,
            };

            // Find or create the tech stack
            let techStack = techStackData.technology_stacks?.find(
              (stack) => stack.tech_stack === stackType
            );

            if (!techStack) {
              techStack = {
                tech_stack: stackType,
                tech_stack_environment: [],
              };
              techStackData.technology_stacks =
                techStackData.technology_stacks || [];
              techStackData.technology_stacks.push(techStack);
            }

            // Find or create the environment
            let environment = techStack.tech_stack_environment?.find(
              (env) => env.environment === envValue
            );

            if (environment) {
              if (!environment.services) {
                environment.services = [];
              }
              // Avoid duplicates by service_name
              const exists = environment.services.some(
                (s) => s.service_name === serviceName
              );
              if (!exists) {
                environment.services.push(service);
              }
            } else {
              techStack.tech_stack_environment =
                techStack.tech_stack_environment || [];
              techStack.tech_stack_environment.push({
                environment: envValue,
                score: "coming soon",
                services: [service],
              });
            }
            // No deviation logic for these entries
            //now added
            // Add to deviations if not Adopt or Mandate
            if (
              envValue === "Proposed" &&
              tech.recommendation !== "Adopt" &&
              tech.recommendation !== "Mandate"
            ) {
              if (!techStackData.deviations) {
                techStackData.deviations = [];
              }

              if (
                !techStackData.deviations.some(
                  (d) => d.service_name === tech.tech_radar_id
                )
              ) {
                techStackData.deviations.push({
                  service_name: tech.tech_radar_id,
                  justification: "",
                });
              }
            }
          }
        }); // Close selectedStackTypes.forEach loop
      }); // Close selectedTechIds.forEach loop

      techStackData.last_modified = new Date().toISOString();

      await callMiddleware("insert", {
        dbName,
        containerName,
        jsonData: techStackData,
        partitionKey: urlAssetId,
      });

      // Note: cloudquery_mapping document is not updated as it's a read-only data source

      setAlertMessage("Technologies added successfully.");
      setAlertType("success");

      // Clear selections
      setSelectedTechnologies({});
      setStackTypeSelections({});
      setSelectedPatterns({});

      // Refresh data
      setTimeout(() => {
        setAlertMessage("");
        fetchTechnologyStacksData();
        // Dispatch a custom event to notify parent to refresh tech stack table
        window.dispatchEvent(new CustomEvent("refresh-tech-stack-table"));
      }, 4000);
    } catch (err) {
      let errorMessage = "Failed to add technologies. ";
      if (err.message) {
        errorMessage += err.message;
      }
      setAlertMessage(errorMessage);
      setAlertType("error");
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch technology stacks data for the current asset
  const fetchTechnologyStacksData = useCallback(async () => {
    if (!urlAssetId) {
      console.error("No asset ID available");
      throw new Error("No asset ID available");
    }

    try {
      const dbName = process.env.REACT_APP_DB_TARGET;
      const containerName = process.env.REACT_APP_CONTAINER_ASSET_PARTITION;
      const query = `SELECT * FROM c WHERE c.asset_id = '${urlAssetId}' AND c.id = 'cloudquery_mapping'`;

      const data = await callMiddleware("get", {
        dbName,
        containerName,
        query,
        partitionKey: urlAssetId,
      });

      // Process cloudquery_mapping data and filter against tech-stacks
      let cloudQueryRecord = null;
      if (data && data.mapping_result) {
        cloudQueryRecord = data;
      } else if (
        Array.isArray(data) &&
        data.length > 0 &&
        data[0].mapping_result
      ) {
        cloudQueryRecord = data[0];
      }

      if (cloudQueryRecord) {
        // Flatten matched and unmatched services across all cloud providers
        const PROVIDER_NORMALIZE = { aws: "Amazon", azure: "Azure", gcp: "Google", oci: "OCI" };
        const allServices = [];
        const cloudProviders = cloudQueryRecord.mapping_result.cloudprovider || [];
        cloudProviders.forEach(({ provider, matched = [], unmatched = [] }) => {
          const normalizedProvider = PROVIDER_NORMALIZE[provider?.toLowerCase()] || provider;
          matched.forEach((svc) => allServices.push({ ...svc, Provider: normalizedProvider }));
          unmatched.forEach((svc) => allServices.push({ ...svc, Provider: normalizedProvider }));
        });

        // Fetch tech-stacks to filter against
        const techStacksData = await callMiddleware("get", {
          dbName,
          containerName,
          query: `SELECT * FROM c WHERE c.asset_id = '${urlAssetId}' AND c.id = 'technology-stacks'`,
          partitionKey: urlAssetId,
        });

        // Build set of existing tech stack services
        let techStackServices = new Set();
        let techStackServiceNames = new Set();

        const techStacksRecord = Array.isArray(techStacksData)
          ? techStacksData[0]
          : techStacksData;
        if (
          techStacksRecord &&
          Array.isArray(techStacksRecord.technology_stacks)
        ) {
          techStacksRecord.technology_stacks.forEach((stack) => {
            if (Array.isArray(stack.tech_stack_environment)) {
              stack.tech_stack_environment.forEach((env) => {
                if (Array.isArray(env.services)) {
                  env.services.forEach((service) => {
                    if (service.service_id) {
                      techStackServices.add(service.service_id);
                    }
                    if (service.service_name) {
                      techStackServiceNames.add(
                        service.service_name.toLowerCase()
                      );
                    }
                  });
                }
              });
            }
          });
        }

        // Filter services that are NOT in tech-stacks (similar to Lucid implementation)
        const filteredServices = allServices.filter((service) => {
          if (service.tech_radar_id) {
            // For matched services, check by tech_radar_id
            return !techStackServices.has(service.tech_radar_id);
          } else {
            // For unmatched services, check by service name
            return !techStackServiceNames.has(service.service.toLowerCase());
          }
        });

        setRuntimeData(filteredServices);

        // Extract timestamp if available
        if (cloudQueryRecord.last_modified) {
          setLastRefreshed(cloudQueryRecord.last_modified);
        }
      } else {
        console.warn("No cloudquery_mapping data found:", data);
        setRuntimeData([]);
      }
    } catch (err) {
      console.error("Error fetching technology stacks data:", err);
      setAlertMessage(
        "Failed to fetch technology stack data. Please try again later."
      );
      setAlertType("error");
      throw err;
    }
  }, [urlAssetId]);

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp);

    const dateStr = date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

    const timeStr = date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZoneName: "short",
    });

    return `${dateStr} ${timeStr}`;
  };

  useEffect(() => {
    fetchTechnologyStacksData();
    fetchTechStackDetails();
  }, [fetchTechnologyStacksData]);

  useEffect(() => {
    // Build filteredIds array for services with tech_radar_id (matched services)
    const ids = runtimeData
      .filter((stack) => stack.tech_radar_id)
      .map((stack) => stack.tech_radar_id);
    setFilteredIds(new Set(ids));
  }, [runtimeData]);

  const filteredData = useMemo(() => {
    const result = [];
    const seenTechNames = new Set();

    // Provider mapping
    const providerMap = {
      "AWS": "Amazon",
      "Azure": "Azure",
      "GCP": "Google",
      "OCI": "OCI",
    };
    const expectedProvider = selectedCloudProvider ? providerMap[selectedCloudProvider] : null;

    filteredIds.forEach((id) => {
      if (techDetailsMap[id]) {
        const tech = { ...techDetailsMap[id] };
        
        // Filter by provider if one is selected
        if (expectedProvider && tech.provider !== expectedProvider) {
          return; // Skip this tech
        }
        
        if (!seenTechNames.has(tech.technologyName)) {
          seenTechNames.add(tech.technologyName);
          result.push(tech);
        }
      }
    });
    // Add entries from runtimeData with display_in_ui true and tech_radar_id null
    if (Array.isArray(runtimeData)) {
      runtimeData.forEach((entry) => {
        if (!entry.tech_radar_id) {
          // Unmatched services (no tech_radar_id)
          const techName = entry.service;
          const entryProvider = entry.Provider || "N/A";
          
          // Filter by provider if one is selected
          if (expectedProvider && entryProvider !== expectedProvider) {
            return; // Skip this entry
          }
          
          if (techName && !seenTechNames.has(techName)) {
            seenTechNames.add(techName);
            result.push({
              tech_radar_id: techName,
              categoryName: "N/A",
              technologyName: techName,
              capability: "N/A",
              recommendation: "Couldn't find in Tech Radar",
              provider: entryProvider,
              supportingPattern: [],
              complianceStatus: "N/A",
              _source: "runtimeData",
              _runtime_entry: entry,
            });
          }
        }
      });
    }
    return result;
  }, [filteredIds, techDetailsMap, runtimeData, selectedCloudProvider]);
  // Pagination state (refactored to match Addtechstack.js)
  const [controlled, setControlled] = useState({
    currentPageIndex: 1,
    itemsPerPage: 5,
    totalItemCount: filteredData.length,
    itemsArray: [5, 10, 20, 50, 100],
  });

  useEffect(() => {
    setControlled((prev) => ({
      ...prev,
      totalItemCount: filteredData.length,
    }));
  }, [filteredData]);

  const paginatedData = useMemo(() => {
    const startIndex =
      (controlled.currentPageIndex - 1) * controlled.itemsPerPage;
    const endIndex = startIndex + controlled.itemsPerPage;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, controlled.currentPageIndex, controlled.itemsPerPage]);

  const onPrevious = () => {
    setControlled((prev) => ({
      ...prev,
      currentPageIndex: Math.max(prev.currentPageIndex - 1, 1),
    }));
  };

  const onNext = () => {
    setControlled((prev) => ({
      ...prev,
      currentPageIndex: Math.min(
        prev.currentPageIndex + 1,
        Math.ceil(prev.totalItemCount / prev.itemsPerPage)
      ),
    }));
  };

  const onChange = (event) => {
    setControlled((prev) => ({
      ...prev,
      currentPageIndex: event.detail,
    }));
  };

  const onItemsPerPageChange = (event) => {
    setControlled((prev) => ({
      ...prev,
      itemsPerPage: event.detail,
      currentPageIndex: 1,
    }));
  };
  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          height: "30px",
          margin: "16px 150px",
        }}
      >
        {openAIAlertOptions && (
          <SafAlert
            appearance={openAIAlertOptions[1]}
            alertType={openAIAlertOptions[0]}
            iconAriaLabel="icon aria label"
            closeAriaLabel="Close"
            onClose={() => setOpenAIAlertOptions(undefined)}
            style={{
              position: "static",
              marginBottom: "40px",
              color: "#003366",
              opacity: 1,
            }}
          >
            {aiAlertMessage}
          </SafAlert>
        )}
      </div>
      <div
        style={{
          maxWidth: "100%",
          margin: "0",
          marginTop: "10px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
            borderBottom: "2px solid #d64000",
            paddingBottom: "10px",
          }}
        >
          <SafText
            appearance="heading-lg"
            style={{
              fontSize: "24px",
              fontWeight: "600",
              margin: 0,
            }}
          >
            Deployed Technology Stacks vs. Current Technology Stacks
          </SafText>
          <SafText
            style={{
              fontSize: "14px",
              color: "#666",
              fontStyle: "italic",
              whiteSpace: "nowrap",
            }}
          >
            Last Refreshed on: {formatDate(lastRefreshed)}
          </SafText>
        </div>

        <div
          style={{
            maxWidth: "100%",
            margin: "10px 0px",
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <div
            style={{
              marginBottom: "10px",
              fontSize: 15,
              color: "#222",
              background: "#fff",
              lineHeight: 1.5,
            }}
          >
            Table shows differences between deployed and current technology
            stacks. Deployed technologies are pulled from Cloudquery. Mismatches
            must be reviewed and approved during the PTO phase.
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "end",
              alignItems: "end",
            }}
          >
            <div style={{ position: "relative", display: "inline-block" }}>
              <SafTooltip anchor="refresh-btn-anchor" placement="top">
                Refresh Current Deployed Technology
              </SafTooltip>
              <SafButton
                id="refresh-btn-anchor"
                style={{ marginRight: "10px", padding: "4px 12px" }}
                appearance="secondary"
                size="small"
                aria-haspopup="dialog"
                aria-controls="button-dialog-overlay"
                onClick={handleRefresh}
                disabled={isLoading}
              >
                <SafIcon
                  slot="start"
                  icon-name="refresh"
                  appearance="solid"
                  presentation
                ></SafIcon>
                Refresh
              </SafButton>
            </div>

            <div style={{ position: "relative", display: "inline-block" }}>
              <SafTooltip anchor="add-btn-anchor" placement="top">
                Add selected technologies to the current stack
              </SafTooltip>
              <SafButton
                id="add-btn-anchor"
                style={{ marginRight: "10px", padding: "4px 12px" }}
                appearance="primary"
                size="small"
                aria-haspopup="dialog"
                aria-controls="button-dialog-overlay"
                onClick={handleAdd}
                disabled={
                  isLoading ||
                  Object.keys(selectedTechnologies).filter(
                    (k) => selectedTechnologies[k]
                  ).length === 0
                }
              >
                <SafIcon
                  slot="start"
                  icon-name="plus"
                  appearance="solid"
                  presentation
                ></SafIcon>
                {isLoading ? "Adding..." : "Add"}
              </SafButton>
            </div>
          </div>
        </div>
        <div style={{ width: "100%", marginTop: "10px" }}>
          <SafTable
            alternating-rows="true"
            density="inherit"
            header-background="default"
            inline-borders="true"
          >
            <thead>
              <tr>
                <th
                  style={{
                    width: "10%",
                    textAlign: "center",
                  }}
                >
                  Add
                </th>
                <th
                  style={{
                    width: "10%",
                    textAlign: "center",
                  }}
                >
                  Category
                </th>
                <th
                  style={{
                    width: "10%",
                    textAlign: "center",
                  }}
                >
                  Technology Name
                </th>
                <th
                  style={{
                    width: "8%",
                    textAlign: "center",
                  }}
                >
                  Capability
                </th>
                <th
                  style={{
                    width: "8%",
                    textAlign: "center",
                  }}
                >
                  Supporting Patterns
                </th>
                <th
                  style={{
                    width: "25%",
                    textAlign: "center",
                  }}
                >
                  Stack Type
                </th>

                <th
                  style={{
                    width: "9%",
                    textAlign: "center",
                  }}
                >
                  Recommendation
                </th>
                <th
                  style={{
                    width: "8%",
                    padding: "4px 8px",
                  }}
                >
                  Compliance Status
                </th>
                <th
                  style={{
                    width: "8%",
                    textAlign: "center",
                  }}
                >
                  Provider
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.length === 0 ? (
                <tr>
                  <td
                    colSpan="9"
                    style={{
                      textAlign: "center",
                      padding: "20px",
                      color: "#666",
                      fontStyle: "italic",
                    }}
                  >
                    No data available
                  </td>
                </tr>
              ) : (
                paginatedData.map((tech) => {
                  return (
                    <tr
                      key={tech.tech_radar_id + tech.serviceName}
                      style={{
                        transition: "all 0.2s ease",
                      }}
                    >
                      <td
                        style={{
                          // width: "100px",
                          textAlign: "center",
                        }}
                      >
                        <SafCheckbox
                          checked={
                            selectedTechnologies[tech.tech_radar_id] || false
                          }
                          onChange={() =>
                            handleCheckboxChange(tech.tech_radar_id)
                          }
                        />
                      </td>
                      <td
                        style={{
                          // width: "200px",
                          textAlign: "center",
                        }}
                      >
                        {tech.categoryName || "N/A"}
                      </td>
                      <td
                        style={{
                          // width: "50px",
                          textAlign: "center",
                        }}
                      >
                        {tech.technologyName || ""}
                      </td>
                      <td
                        style={{
                          // width: "100px",
                          textAlign: "center",
                        }}
                      >
                        {tech.capability || "N/A"}
                      </td>
                      <td
                        style={{
                          // width: "100px",
                          textAlign: "center",
                        }}
                      >
                        {tech.supportingPattern &&
                        tech.supportingPattern.length > 0 ? (
                          <div
                            style={{
                              // width: "200px",
                              display: "flex",
                              flexDirection: "column",
                              gap: "8px",
                            }}
                          >
                            {tech.supportingPattern.map(
                              (pattern, patternIndex) => (
                                <div
                                  key={patternIndex}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                  }}
                                >
                                  <SafCheckbox
                                    checked={
                                      selectedPatterns[
                                        tech.tech_radar_id
                                      ]?.includes(patternIndex) || false
                                    }
                                    onChange={(e) =>
                                      handlePatternCheckboxChange(
                                        tech.tech_radar_id,
                                        patternIndex,
                                        e.target.checked
                                      )
                                    }
                                    disabled={
                                      !selectedTechnologies[tech.tech_radar_id]
                                    }
                                  />
                                  <SafAnchor
                                    href={`https://techtoc.thomsonreuters.com/${pattern.pattern_link}/`}
                                    target="_blank"
                                    style={{ fontSize: "0.875rem" }}
                                  >
                                    {pattern.pattern_name}
                                  </SafAnchor>
                                </div>
                              )
                            )}
                          </div>
                        ) : (
                          "N/A"
                        )}
                      </td>
                      <td
                        style={{
                          textAlign: "center",
                        }}
                      >
                        <SafCombobox
                          id={`combobox-stack-type-${tech.tech_radar_id}`}
                          multiple={true}
                          value=""
                          placeholder={
                            stackTypeSelections[tech.tech_radar_id]?.length > 0
                              ? stackTypeSelections[tech.tech_radar_id].join(
                                  ", "
                                )
                              : "Select..."
                          }
                          disabled={!selectedTechnologies[tech.tech_radar_id]}
                          style={{ width: "260px" }}
                        >
                          {stackTypeOptions.map((option) => (
                            <SafOption
                              key={option.id}
                              selected={(
                                stackTypeSelections[tech.tech_radar_id] || []
                              ).includes(option.label)}
                              onClick={() => {
                                handleStackTypeChange(
                                  tech.tech_radar_id,
                                  option.label
                                );
                              }}
                            >
                              {option.label}
                            </SafOption>
                          ))}
                        </SafCombobox>
                      </td>

                      <td
                        style={{
                          // width: "100px",
                          textAlign: "center",
                        }}
                      >
                        {tech.recommendation || "N/A"}
                      </td>
                      <td
                        style={{
                          // width: "100px",
                          textAlign: "center",
                        }}
                      >
                        {tech.complianceStatus || "N/A"}
                      </td>
                      <td
                        style={{
                          // width: "100px",
                          textAlign: "center",
                        }}
                      >
                        {tech.provider || "N/A"}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </SafTable>
        </div>
        {/* Pagination controls (refactored to use SafPagination) */}
        <div className="Component-demo" style={{ margin: "16px 0" }}>
          <SafPagination
            data-testid="controlled"
            controlled={true}
            totalItemCount={controlled.totalItemCount}
            currentPageIndex={controlled.currentPageIndex}
            itemsArray={controlled.itemsArray}
            itemsPerPage={controlled.itemsPerPage}
            onPrevious={onPrevious}
            onNext={onNext}
            onChange={onChange}
            onItemsPerPageChange={onItemsPerPageChange}
            style={{ width: "95%" }}
          />
        </div>

        {alertMessage && (
          <div
            style={{ position: "fixed", top: "0%", right: "40%", zIndex: 1000 }}
          >
            <SafAlert
              type="toast"
              appearance={alertType}
              onClose={() => setAlertMessage("")}
              duration="5"
            >
              {alertMessage}
            </SafAlert>
          </div>
        )}
      </div>
    </>
  );
};

export default TechnologyStackComparison;
